import {
  framesFromTimeOfDayMs, timeOfDayMs, type ClockReading, type TimecodeSpec,
} from "@infoflyt/shared/timecode";
import { supabase } from "./supabase";
import { oektIdFor } from "@infoflyt/shared/logg";

/**
 * Loggen som skrives FRA NETTFLATA.
 *
 * ========  HVEM SKRIVER HER  ========
 *
 * Reporteren synker (`synk.ts`), og fra 26.10.2026 logger crew direkte fra
 * nettflata på Livelogg-flata. Begge skriver i SIN EGEN logg for dagen, og
 * begge går gjennom denne modulen — det er ett sted som vet hvordan en
 * loggpost fra nett blir til.
 *
 * `CLAUDE.md` sier at loggen går én vei: opp. Regelen er presisert
 * 24.10.2026 til å gjelde per logg og dens eget opphav. Det som ikke er
 * endret: INGEN SKRIVER I EN ANNEN MANNS LOGG. Alt her skriver bare til den
 * innloggedes egen økt.
 *
 * ========  TIDSKODEN ER PRODUKSJONENS  ========
 *
 * Ankeret hører til (produksjon, opptaksdag), og det som gjelder er det
 * nyeste noen har JAMMET. Er man på samme produksjon, har man samme
 * tidskode. En PC er ikke jammet mot noe, så `timecodeAt` regner forløpt tid
 * fra veggklokka og setter `usikker` — riktig svar, ikke en nødløsning.
 */

/**
 * Boot-id for DENNE sidelastingen.
 *
 * `performance.now()` starter på null ved hver lasting, så en id som overlevde
 * en omlasting ville gjort monotone avlesninger usammenlignbare. Det er
 * nøyaktig samme betydning som `boot_id` har på telefonen: «samme ubrutte
 * klokke». Derfor ny ved hver lasting, med vilje.
 */
const BOOT = crypto.randomUUID();

/** Avlesningen nå. Monoton klokke, ikke `Date.now()` — se CLAUDE.md. */
export function naa(): ClockReading {
  return { monotonicMs: performance.now(), bootId: BOOT, wallUtcMs: Date.now() };
}

export interface Anker {
  readonly id: string;
  readonly tcFrames: number;
  readonly framerate: string;
  readonly dropFrame: boolean;
  readonly monotonicMs: number;
  readonly bootId: string;
  readonly wallUtcMs: number;
}


export function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}


/**
 * Produksjonens tidskode for dagen, eller et nytt anker.
 *
 * ER MAN PÅ SAMME PRODUKSJON, HAR MAN SAMME TIDSKODE. Avgjort 25.10.2026.
 * Det som gjelder er det nyeste ankeret noen har JAMMET — `kind = 'jam'`.
 * Avledede ankere er enhetenes egne uttrykk for den samme verdien, og å
 * plukke et av dem ville gitt reporteren en annen tidskode enn regi.
 *
 * Finnes ingen jam, settes et nytt til PC-klokka — men som `kind = 'klokke'`,
 * og det er en viktig forskjell fra før 27.10.2026. Et klokkeanker gjelder
 * BARE denne flata. Det følger den ufravikelige regelen «ny logg starter med
 * tidskoden satt til nåværende klokkeslett» — det finnes aldri en post uten
 * tidskode — uten at PC-klokka dermed blir hele produksjonens tidskode.
 * Se migrasjon 20261027091000.
 */
/**
 * Produksjonens jam for dagen, uten å lage noe.
 *
 * Brukes av toppstripa, som bare skal LESE. Den sto med et hardkodet frø
 * fram til 25.10.2026 — «ankeret er frøet til nettflata får synk» — og viste
 * derfor en helt annen tidskode enn den som ble lagret i loggen.
 */
export async function jamFor(
  produksjonId: string, opptaksdag: string,
): Promise<Anker | null> {
  if (supabase === null) return null;
  const { data, error } = await supabase
    .from("tc_anchor")
    .select("id, tc_frames, framerate, drop_frame, monotonic_ms, boot_id, wall_utc")
    .eq("production_id", produksjonId)
    .eq("shoot_day", opptaksdag)
    .eq("kind", "jam")
    .order("wall_utc", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error !== null || data === null) return null;
  const r = data as unknown as Record<string, unknown>;
  return {
    id: str(r["id"]),
    tcFrames: Number(r["tc_frames"]),
    framerate: str(r["framerate"]),
    dropFrame: Boolean(r["drop_frame"]),
    monotonicMs: Number(r["monotonic_ms"]),
    bootId: str(r["boot_id"]),
    wallUtcMs: Date.parse(str(r["wall_utc"])),
  };
}

/**
 * MITT EGET KLOKKEANKER FOR DAGEN.
 *
 * ========  HVORFOR DETTE OPPSLAGET MAA FINNES  ========
 *
 * `ankerFor` spurte etter en JAM, og fant den ikke — for ingen hadde
 * jammet — hvorpå den satte inn et nytt klokkeanker. Hver gang. Og
 * Livelogg-flata lytter på `tc_anchor`, så innsettingen vekket
 * `useLevende`, som kalte `ankerFor` på nytt, som satte inn enda ett.
 *
 * Det er en sløyfe som går så fort som nettet tillater, og den hadde
 * gått i noen dager: `tc_anchor` sto med 4,7 MB rader mens alle andre
 * tabeller i basen sto på 16 kB. Det var den som gjorde at en
 * produksjon ikke lot seg slette — se migrasjon 20261101100000.
 *
 * ========  HVORFOR DET HOLDER AA LESE DEN GAMLE  ========
 *
 * Raden gjenbrukes som den er, også etter en omlasting der `boot_id` er
 * en annen. På en PC koster det ingenting: nettflata er aldri jammet
 * mot noe, så `timecodeAt` regner uansett forløpt tid fra veggklokka og
 * setter `usikker` — det står i toppen av denne fila, og det er riktig
 * svar og ikke en nødløsning. Ett klokkeanker per (produksjon, dag,
 * person), og ikke ett per sidelasting.
 */
async function minKlokke(
  produksjonId: string, opptaksdag: string, medlemId: string,
): Promise<Anker | null> {
  if (supabase === null) return null;
  const { data, error } = await supabase
    .from("tc_anchor")
    .select("id, tc_frames, framerate, drop_frame, monotonic_ms, boot_id, wall_utc")
    .eq("production_id", produksjonId)
    .eq("shoot_day", opptaksdag)
    .eq("author_id", medlemId)
    .eq("kind", "klokke")
    .order("wall_utc", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error !== null || data === null) return null;
  const r = data as unknown as Record<string, unknown>;
  return {
    id: str(r["id"]),
    tcFrames: Number(r["tc_frames"]),
    framerate: str(r["framerate"]),
    dropFrame: Boolean(r["drop_frame"]),
    monotonicMs: Number(r["monotonic_ms"]),
    bootId: str(r["boot_id"]),
    wallUtcMs: Date.parse(str(r["wall_utc"])),
  };
}

export async function ankerFor(
  produksjonId: string, opptaksdag: string, medlemId: string, spec: TimecodeSpec,
  framerate: string, dropFrame: boolean,
): Promise<Anker | null> {
  if (supabase === null) return null;

  const funnet = await jamFor(produksjonId, opptaksdag);
  if (funnet !== null) return funnet;

  /* JAMMEN FOERST, SAA MIN EGEN KLOKKE, OG FOERST DA ET NYTT ANKER.
     Uten midterste ledd lages det et nytt anker ved hvert kall. */
  const mitt = await minKlokke(produksjonId, opptaksdag, medlemId);
  if (mitt !== null) return mitt;

  const n = naa();
  const nytt = {
    id: crypto.randomUUID(),
    production_id: produksjonId,
    author_id: medlemId,
    shoot_day: opptaksdag,
    tc_frames: framesFromTimeOfDayMs(timeOfDayMs(new Date(n.wallUtcMs)), spec),
    framerate,
    drop_frame: dropFrame,
    monotonic_ms: n.monotonicMs,
    boot_id: n.bootId,
    wall_utc: new Date(n.wallUtcMs).toISOString(),
    source: "manual" as const,
    /* PC-klokka, og ingenting annet. `aligned_to` er proveniensen klipperen
       trenger for å vite hva tidskoden ble satt MOT. */
    aligned_to: "telefon" as const,
    /* KLOKKE, IKKE JAM. Ingen har jammet ennå, og da går denne flata på sin
       egen klokke til noen gjør det — uten at de andre adopterer den.
       Fram til 27.10.2026 sto dette som en jam, og da kunne PC-klokka til
       den som tilfeldigvis åpnet fana først bli produksjonens tidskode. */
    kind: "klokke" as const,
    derived_from: null,
  };
  const { error: feil } = await supabase.from("tc_anchor").insert(nytt);
  /*
   * NEKTET ER IKKE DET SAMME SOM MISLYKKET.
   *
   * Basen holder ett klokkeanker per person per dag —
   * `tc_anchor_ett_klokkeanker`, migrasjon 20261101150000. To faner
   * aapne samtidig moeter den, og da finnes svaret alt: det er bare
   * ikke vaart. Vi leser det i stedet for aa gi opp, for et null her
   * er en flate uten tidskode.
   */
  if (feil !== null) return minKlokke(produksjonId, opptaksdag, medlemId);
  return {
    id: nytt.id,
    tcFrames: nytt.tc_frames,
    framerate,
    dropFrame,
    monotonicMs: n.monotonicMs,
    bootId: n.bootId,
    wallUtcMs: n.wallUtcMs,
  };
}

/**
 * Reporterens egen økt for dagen, eller en ny.
 *
 * ÉN FORFATTER PER LOGG. Økten slås opp på (produksjon, dag, forfatter), slik
 * at reporteren fortsetter i sin egen logg og ikke havner i noen annens.
 */

/**
 * Oekta man skriver i for en dag.
 *
 * ========  ER MAN PAA ET TEAM, ER DEN TEAMETS  ========
 *
 * Avgjort 29.10.2026. Hele LET 2 skriver i den samme loggen, og rapporten
 * blir teamets — den henger paa én `log_session`, saa delt rapport
 * forutsetter delt oekt.
 *
 * Id-en SLAAS IKKE OPP for et team, den regnes ut: `oektIdFor` utleder den
 * av (produksjon, dag, team). Nettflata hadde strengt tatt klart et
 * oppslag — den er alltid paa nett — men appen har det ikke, og de to maa
 * lande paa samme rad. Én utregning, to flater.
 *
 * Uten team er loggen din egen, og da slaas den opp paa forfatter som foer.
 */
export async function finnOekt(
  produksjonId: string, opptaksdag: string, medlemId: string,
  teamId: string | null = null,
): Promise<string | null> {
  if (supabase === null) return null;

  const teamets = oektIdFor(produksjonId, opptaksdag, teamId);
  /*
   * ========  EN LEVERT RAPPORT LUKKER IKKE DAGEN  ========
   *
   * Her sto `.eq("status", "open")`, og det var feilen brukeren fant
   * 02.11.2026: «Hvis jeg som regi skriver i liveloggen saa forsvinner
   * postene jeg sender, men de kommer inn i team-loggen under logg.»
   *
   * Leverer appen rapporten, settes oekta til `closed`. Da fant dette
   * oppslaget ingenting — flata toemte lista — mens `aapneOekt` regnet seg
   * fram til den SAMME id-en og skrev postene inn i den lukkede oekta.
   * Postene var altsaa der hele tiden; det var liveloggen som ikke kunne
   * se dem. Redaktoerens loggflate leser med vilje ogsaa lukkede oekter.
   *
   * «En levert rapport setter en strek i loggen» — den er et oeyeblikksbilde,
   * og loggen skrives videre etterpaa. Se CLAUDE.md.
   *
   * SORTERT, ikke tilfeldig: uten en rekkefoelge avgjoer Postgres hvilken
   * rad som kommer foerst, og med to oekter paa samme dag kunne svaret
   * byttet mellom to henkinger.
   */
  const spoerring = supabase
    .from("log_session")
    .select("id")
    .eq("production_id", produksjonId)
    .eq("shoot_day", opptaksdag);

  const { data, error } = await (teamets === null
    ? spoerring.eq("author_id", medlemId)
    : spoerring.eq("id", teamets))
    .order("opened_at", { ascending: false }).limit(1).maybeSingle();
  if (error !== null || data === null) return null;
  return str((data as unknown as Record<string, unknown>)["id"]);
}

/**
 * Oekta, og den LAGES bare naar noe skal skrives.
 *
 * HER LAA EN BIVIRKNING. `hent` opprettet oekta, saa det aa aapne fanen var
 * nok til aa aapne en logg for dagen — og en reporter som bare kikket la
 * igjen en tom spalte paa redaktoerens loggflate. Tre av dem ble til paa en
 * halv dags proeving. Rettet 25.10.2026.
 *
 * Naa leser `hent` bare, og dette kalles fra «Start synk».
 */
export async function aapneOekt(
  produksjonId: string, opptaksdag: string, medlemId: string, ankerId: string,
  teamId: string | null = null,
): Promise<string | null> {
  const finnes = await finnOekt(produksjonId, opptaksdag, medlemId, teamId);
  if (finnes !== null) return finnes;
  if (supabase === null) return null;

  const teamets = oektIdFor(produksjonId, opptaksdag, teamId);
  const id = teamets ?? crypto.randomUUID();

  /*
   * `ignoreDuplicates` PAA EN TEAMOEKT.
   *
   * To paa samme team kan trykke foerste gang i samme sekund, og begge
   * regner seg fram til samme id. Den andre skal da ikke overskrive den
   * foerste — navnet paa oekta, ankeret og aapningstidspunktet hoerer den
   * som kom foerst til. En kollisjon er normalen her, ikke en feil.
   */
  const { error } = await supabase.from("log_session").upsert({
    id,
    production_id: produksjonId,
    unit_id: teamId,
    author_id: medlemId,
    anchor_id: ankerId,
    shoot_day: opptaksdag,
    status: "open",
    opened_at: new Date().toISOString(),
  }, { ignoreDuplicates: true });
  return error === null ? id : null;
}


/**
 * Rammene en post skrives inn i.
 *
 * Oekta kan vaere null: den lages av den FOERSTE posten. Aa aapne en fane
 * skal ikke aapne en logg — det la igjen tomme spalter paa redaktoerens
 * loggflate, se `aapneOekt`.
 */
export interface Rammer {
  readonly oektId: string | null;
  /** Teamet, eller null. Avgjoer hvilken oekt posten havner i. */
  readonly teamId?: string | null;
  readonly produksjonId: string;
  readonly opptaksdag: string;
  readonly ankerId: string;
  /**
   * Boot-id-en ankeret ble satt med.
   *
   * Avgjoer om posten er `ok` eller `usikker`. Satte en regissoer ankeret
   * mot boksen fra telefonen sin, er den aldri lik vaar — og det er riktig:
   * vi regner fra veggklokka da.
   */
  readonly ankerBootId: string;
  readonly medlemId: string;
  /**
   * Navnet vaart. Staar paa posten til neste innlesing henter det.
   *
   * Valgfritt: `synk.Rammer` er en egen type med sin egen skrivevei, og
   * reporterens logg har bare én forfatter uansett.
   */
  readonly forfatterNavn?: string | null;
}

/** En kommentar fra redaktoeren. Samme form som i state/logg.ts. */
export interface Kommentar {
  readonly id: string;
  readonly tekst: string;
  readonly forfatter: string;
}

/** Et synkspoersmaal redaktoeren skrev ut av posten. */
export interface Spoersmaal {
  readonly id: string;
  readonly tekst: string;
  readonly deltaker: string | null;
}

export interface Loggpost {
  readonly id: string;
  readonly deltakerId: string | null;
  readonly label: string;
  readonly farge: string | null;
  readonly tekst: string;
  readonly monotonicMs: number;
  readonly bootId: string;
  readonly wallUtcMs: number;
  readonly tcUsikker: boolean;
  /** Navnet paa den som skrev DENNE posten. Null naar det ikke er hentet. */
  readonly forfatter: string | null;
  /** Medlemsraden som skrev posten. Avgjoer om flata tilbyr retting. */
  readonly forfatterId: string;
  /**
   * ========  DET REDAKTOEREN HAR LAGT IGJEN  ========
   *
   * Brukeren 02.11.2026: «Naar man liker/skriver til regi/lager synk-
   * spoersmaal under logg i nettflata til liveloggen, saa faar ikke
   * liveloggen det opp fortloepende.»
   *
   * Tre ting, samme plass — kommentar, hjerte og et spoersmaal skrevet ut
   * av posten. De hoerer til posten og vises paa den, men de er ikke
   * loggen: egne tabeller, og ingen skrivevei inn i `log_entry`. Appen har
   * vist dem siden 24.10.2026 (`state/tilbakemelding.ts`); liveloggen er
   * den samme flata for den som sitter inne, og maatte ha dem ogsaa.
   *
   * EN MERKNAD KAN ALDRI KREVE NOE. Ingen hake, ingen tilstand aa gjoere
   * opp, ingenting som hindrer at dagen avsluttes.
   */
  readonly kommentarer: readonly Kommentar[];
  readonly markert: boolean;
  readonly spoersmaal: readonly Spoersmaal[];
  /**
   * Flata posten BLE TIL paa: «app», «web», eller tom for en rad fra foer
   * kolonnen fantes.
   *
   * Loggen gaar én vei, og originalen ligger paa opphavsflata. Teksten kan
   * derfor bare rettes her om posten ble skrevet her. Se migrasjon
   * 20261102210000 og `endreTekst`.
   */
  readonly opphav: string;
}

export function tilLoggpost(r: Record<string, unknown>): Loggpost {
  return {
    id: str(r["id"]),
    deltakerId: r["participant_id"] == null ? null : str(r["participant_id"]),
    label: str(r["label"]),
    farge: r["color"] == null ? null : str(r["color"]),
    tekst: str(r["text"]),
    monotonicMs: Number(r["monotonic_ms"]),
    bootId: str(r["boot_id"]),
    wallUtcMs: Date.parse(str(r["wall_utc"])),
    tcUsikker: str(r["tc_state"]) === "usikker",
    forfatterId: str(r["author_id"]),
    kommentarer: ((r["entry_comment"] ?? []) as Record<string, unknown>[]).map((k) => ({
      id: str(k["id"]),
      tekst: str(k["text"]),
      forfatter: str((k["production_member"] as { display_name?: string } | null)
        ?.display_name ?? ""),
    })),
    markert: ((r["entry_mark"] ?? []) as unknown[]).length > 0,
    spoersmaal: ((r["su_question"] ?? []) as Record<string, unknown>[]).map((q) => ({
      id: str(q["id"]),
      tekst: str(q["text"]),
      deltaker: ((): string | null => {
        const d = (q["participant"] as { abbreviation?: string } | null)?.abbreviation;
        return typeof d === "string" && d !== "" ? d : null;
      })(),
    })),
    opphav: str(r["origin"]),
    forfatter: ((): string | null => {
      const f = r["forfatter"] as { display_name?: string } | null | undefined;
      const navn = f?.display_name;
      return typeof navn === "string" && navn !== "" ? navn : null;
    })(),
  };
}

const FELT = "id, participant_id, label, color, text, monotonic_ms, boot_id,"
  + " wall_utc, tc_state, author_id, origin,"
  /* Redaktoerens tre. Innleiret i samme svar — én runde per post ville
     gitt hundrevis av foresporsler paa en travel dag, og det er samme
     grep `hentLogger` bruker. */
  + " entry_comment(id, text, created_at, production_member(display_name)),"
  + " entry_mark(author_id),"
  + " su_question(id, text, participant(abbreviation)),"
  /* Hvem som skrev DENNE posten. Trengs for hodet paa liveloggen: den
     foerste som skrev staar som forfatter, og paa et team er det ofte en
     annen enn den som leser. Se LiveloggScreen. */
  + " forfatter:production_member!log_entry_author_id_fkey(display_name)";

/**
 * Den FOERSTE som skrev i oekta, eller null.
 *
 * ========  HVORFOR DEN FOERSTE, OG IKKE DEN SOM AAPNET  ========
 *
 * Brukeren 02.11.2026: «I liveloggen skal det vaere den foerste som
 * skriver en post som staar som forfatter etter team navnet, siden det
 * ofte ikke er regi som logger der.»
 *
 * `log_session.author_id` duger ikke til dette: paa en teamlogg settes
 * den til REGIEN av en utloeser, uansett hvem som skrev. Det er riktig
 * for loggen som helhet — den er regiens — men liveloggen er flata der
 * man SKRIVER, og der er den foerste posten svaret.
 */
export function foersteForfatter(poster: readonly Loggpost[]): string | null {
  return poster.find((p) => p.forfatter !== null)?.forfatter ?? null;
}

/** Postene i én oekt, eldste foerst. Tombstoner er filtrert bort. */
export async function hentPoster(oektId: string): Promise<Loggpost[]> {
  if (supabase === null) return [];
  const { data, error } = await supabase
    .from("log_entry")
    .select(FELT)
    .eq("session_id", oektId)
    .is("deleted_at", null)
    .order("wall_utc", { ascending: true });
  if (error !== null) return [];
  return (data ?? []).map((r) => tilLoggpost(r as unknown as Record<string, unknown>));
}

/**
 * Skriver én post i egen logg.
 *
 * TIDSKODEN FANGES VED TRYKKET, ikke ved lagringen: avlesningen tas av den
 * som kaller, og foelger posten til basen. Gaar nettet, er det avlesningen
 * fra trykket som staar — ikke tidspunktet svaret kom fram. Det er den
 * ufravikelige regelen, og den gjelder her som i appen.
 *
 * Returnerer oekta posten havnet i, slik at den som kalte kan ta vare paa
 * den: FOERSTE post aapner oekta.
 */
export async function skrivPost(
  r: Rammer,
  felt: {
    readonly label: string;
    readonly farge: string | null;
    readonly tekst: string;
    readonly deltakerId?: string | null;
  },
  lest: ClockReading,
): Promise<{ readonly post: Loggpost; readonly oektId: string } | null> {
  if (supabase === null) return null;
  const oektId = r.oektId
    ?? await aapneOekt(r.produksjonId, r.opptaksdag, r.medlemId, r.ankerId,
                       r.teamId ?? null);
  if (oektId === null) return null;

  const rad = {
    id: crypto.randomUUID(),
    session_id: oektId,
    production_id: r.produksjonId,
    anchor_id: r.ankerId,
    author_id: r.medlemId,
    label: felt.label,
    color: felt.farge,
    text: felt.tekst,
    monotonic_ms: lest.monotonicMs,
    boot_id: lest.bootId,
    wall_utc: new Date(lest.wallUtcMs).toISOString(),
    /* Satte en ANNEN enhet ankeret, er tidskoden regnet fra veggklokka og
       dermed usikker — og posten skal si det. `timecodeAt` kommer til samme
       svar av seg selv, men feltet er det klipperen leser. */
    tc_state: lest.bootId === r.ankerBootId ? "ok" : "usikker",
    participant_id: felt.deltakerId ?? null,
    /*
     * HVOR POSTEN BLE TIL.
     *
     * Her er originalen, og derfor er dette den eneste flata som kan rette
     * teksten paa den. Appens poster baerer «app» og roeres ikke herfra —
     * se `endreTekst` og migrasjon 20261102210000.
     */
    origin: "web",
  };
  const { error } = await supabase.from("log_entry").insert(rad);
  if (error !== null) return null;
  return {
    oektId,
    post: {
      id: rad.id,
      deltakerId: rad.participant_id,
      label: felt.label,
      farge: felt.farge,
      tekst: felt.tekst,
      monotonicMs: lest.monotonicMs,
      bootId: lest.bootId,
      wallUtcMs: lest.wallUtcMs,
      tcUsikker: rad.tc_state === "usikker",
      forfatterId: r.medlemId,
      opphav: rad.origin,
      /* Ingen har sett den ennaa. Neste innlesing henter merknadene. */
      kommentarer: [],
      markert: false,
      spoersmaal: [],
      /* Posten vi nettopp skrev er vaar egen. Navnet hentes ved neste
         innlesing; her ville et oppslag bare bekreftet det vi vet. */
      forfatter: r.forfatterNavn ?? null,
    },
  };
}

/**
 * Trekker en post tilbake.
 *
 * TOMBSTONE, IKKE SLETTING. Loggen er append-only, og det gjelder her ogsaa.
 * Posten blir staaende med `deleted_at`, og flatene filtrerer den bort. Se
 * CLAUDE.md.
 *
 * Dette er loggens EGEN flate som trekker tilbake sin EGEN post — ingen
 * roerer noe de ikke har skrevet.
 */
export async function trekkTilbake(postId: string): Promise<boolean> {
  if (supabase === null) return false;
  /*
   * `.select()` ER IKKE PYNT HER.
   *
   * Policyen slipper bare gjennom din egen post i din egen oekt, og en
   * `update` som ikke treffer noen rad gir INGEN feil — den svarer at
   * alt gikk bra og endrer ingenting. Flata sa dermed ja, lista lastet
   * om, og posten sto der fortsatt. Brukeren 02.11.2026: «De faar ikke
   * jeg fjernet.»
   *
   * Naa teller vi radene som faktisk ble truffet. Null betyr at posten
   * ikke er din — en annen konto har skrevet den, og da er det den som
   * maa trekke den tilbake.
   */
  const { data, error } = await supabase
    .from("log_entry")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", postId)
    .select("id");
  return error === null && (data ?? []).length > 0;
}


/**
 * Fyller ut teksten paa en post man alt har sendt.
 *
 * ========  HVORFOR DETTE ER ET RPC OG IKKE EN UPDATE  ========
 *
 * Brukeren 02.11.2026: «om det er hektisk naar man jobber, saa lagrer man
 * bare postene og utfyller de foer man sender rapporten.» Det er den samme
 * retten appen har hatt hele tiden — tidskoden fanges ved trykket, teksten
 * skrives naar man har tid.
 *
 * To ting kan ikke gjoeres herfra, og de er grunnen til funksjonen:
 *
 * `log_entry` har ingen `updated_at`. Tekstendringer skal i `audit_log`, og
 * den tabellen har RLS paa og INGEN policy — ingen klient kommer til, med
 * vilje. Appen foerer sin egen bok lokalt; nettflata har ingen.
 *
 * Og posten maa rettes DER DEN BLE SKREVET. Et team deler oekt, saa lista
 * her inneholder ogsaa poster fra telefonene — blant dem dine egne. Rettet
 * man en av dem her, ville appens neste synk skrevet sin gamle tekst
 * tilbake (`sendPost` er en `upsert` paa hele raden), og de to flatene vist
 * ulik logg uten at noe sa fra.
 *
 * Funksjonen svarer paa norsk naar den ikke kan. Teksten vises som den er.
 */
export async function endreTekst(
  postId: string, tekst: string,
): Promise<{ readonly tekst: string } | { readonly feil: string }> {
  if (supabase === null) return { feil: "Ingen forbindelse til basen." };
  const { data, error } = await supabase.rpc("endre_loggtekst", {
    p_id: postId, p_tekst: tekst,
  });
  if (error !== null) return { feil: error.message };
  return { tekst: typeof data === "string" ? data : tekst };
}
